import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { FormattedText } from '../../../../components/common/FormattedText/FormattedText';
import { Typography } from '../../../../components/common/Typography/Typography';
import { LinkType, ModCategory } from '../../../../constants/enums';
import { APP_ROUTES } from '../../../../constants/routes';
import { useActiveSection } from '../../../../hooks/useActiveSection/useActiveSection';
import { useDocumentHead } from '../../../../hooks/useDocumentHead/useDocumentHead';
import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { sanitizeUrl } from '../../../../services/urlSafety';
import { toCssUrl } from '../../../../services/urlSafety';
import { useGetModByIdQuery } from '../../api/modsApi';
import { CollapsibleSection } from '../../components/CollapsibleSection/CollapsibleSection';
import { PageSideNav } from '../../components/PageSideNav/PageSideNav';
import {
  getLinkTypeIconPath,
  getLinkTypeLabel,
  isExternalLinkType,
} from '../../utils/linkTypeUtils';
import {
  getThumbnailSrc,
  getYoutubeEmbedUrls,
  getYoutubeThumbnailUrl,
} from '../../utils/mediaUtils';
import styles from './ModProfileScreen.module.css';
import { ModProfileScreenProps } from './ModProfileScreen.types';

interface UnifiedMediaItem {
  id: string;
  type: 'video' | 'image';
  src: string;
  thumbnailUrl: string;
  alt: string;
  label: string;
}

export const ModProfileScreen: React.FC<ModProfileScreenProps> = ({
  id: propId,
  className = '',
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const modId = propId || paramId || '';
  const { t } = useTranslation();

  const { data: mod, isLoading, error } = useGetModByIdQuery(modId);

  const bgThumbnail =
    (mod?.thumbnailUrl && mod.thumbnailUrl.trim()) ||
    (mod?.images && mod.images.length > 0 && mod.images[0]?.trim()) ||
    undefined;
  const bgUrl = bgThumbnail ? getThumbnailSrc(bgThumbnail) : undefined;

  useDocumentHead({
    title: mod ? `${mod.title} - ${t.brand} VR Mods` : undefined,
    description: mod
      ? mod.description || t.seo.modProfileDescription({ title: mod.title })
      : undefined,
    ogImage: mod?.thumbnailUrl ? getThumbnailSrc(mod.thumbnailUrl) : undefined,
  });

  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const thumbnailsRowRef = useRef<HTMLDivElement>(null);
  const [userSelectedNavId, setUserSelectedNavId] = useState<string | null>(null);

  const youtubeEmbedUrls = useMemo(
    () => (mod ? getYoutubeEmbedUrls(mod.youtubeUrls || mod.youtubeUrl) : []),
    [mod]
  );
  const imagesList = useMemo(
    () =>
      mod
        ? (mod.images && mod.images.length > 0 ? mod.images : [])
            .filter((img) => img && img !== mod.thumbnailUrl)
            .map((img) => getThumbnailSrc(img))
        : [],
    [mod]
  );

  const mediaList = useMemo<UnifiedMediaItem[]>(() => {
    if (!mod) return [];
    const list: UnifiedMediaItem[] = [];

    // 1. Videos first
    youtubeEmbedUrls.forEach((embedUrl, idx) => {
      const ytThumb = getYoutubeThumbnailUrl(embedUrl);
      list.push({
        id: `video-${idx}-${embedUrl}`,
        type: 'video',
        src: embedUrl,
        thumbnailUrl: ytThumb || '',
        alt: t.mods.profile.videoPreviewAlt({ title: mod.title, index: idx + 1 }),
        label: t.mods.profile.videoLabel({ index: idx + 1 }),
      });
    });

    // 2. Images second
    imagesList.forEach((imgSrc, idx) => {
      list.push({
        id: `image-${idx}-${imgSrc}`,
        type: 'image',
        src: imgSrc,
        thumbnailUrl: imgSrc,
        alt: t.mods.profile.imagePreviewAlt({ title: mod.title, index: idx + 1 }),
        label: `Image ${idx + 1}`,
      });
    });

    return list;
  }, [mod, youtubeEmbedUrls, imagesList, t]);

  const hasMedia = mediaList.length > 0;
  const activeMedia = mediaList[selectedMediaIndex] || mediaList[0] || null;

  // Reset selectedMediaIndex on mod ID change
  const [prevModId, setPrevModId] = useState(modId);
  if (modId !== prevModId) {
    setPrevModId(modId);
    setSelectedMediaIndex(0);
    setUserSelectedNavId(null);
  }

  const handleSelectMedia = (index: number) => {
    setSelectedMediaIndex(index);
    if (thumbnailsRowRef.current) {
      const thumbButtons = thumbnailsRowRef.current.querySelectorAll<HTMLButtonElement>(
        `.${styles.thumbnailBtn}`
      );
      if (thumbButtons[index] && typeof thumbButtons[index].scrollIntoView === 'function') {
        thumbButtons[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  };

  const handlePrevMedia = () => {
    if (mediaList.length <= 1) return;
    const nextIndex = (selectedMediaIndex - 1 + mediaList.length) % mediaList.length;
    handleSelectMedia(nextIndex);
  };

  const handleNextMedia = () => {
    if (mediaList.length <= 1) return;
    const nextIndex = (selectedMediaIndex + 1) % mediaList.length;
    handleSelectMedia(nextIndex);
  };

  const downloadOptions = useMemo(
    () =>
      mod
        ? mod.downloadOptions && mod.downloadOptions.length > 0
          ? mod.downloadOptions
          : mod.downloadUrl || mod.link
            ? [
                {
                  name: mod.title,
                  link: (mod.downloadUrl || mod.link) as string,
                  linkType: mod.linkType || LinkType.EXTERNAL,
                  description: mod.description,
                },
              ]
            : []
        : [],
    [mod]
  );

  const hasDescription = Boolean(mod?.description && mod.description.trim());
  const hasInstallationGuide = Boolean(mod?.installationGuide && mod.installationGuide.trim());
  const hasKnownIssues = Boolean(mod?.knownIssues && mod.knownIssues.trim());
  const hasPrivacy = Boolean(mod?.privacy && mod.privacy.trim());
  const hasDisclaimer = Boolean(mod?.disclaimer && mod.disclaimer.trim());
  const hasCredits = Boolean(mod?.credits && mod.credits.trim());
  const hasDownloads = downloadOptions.length > 0;

  const navItems = useMemo(
    () =>
      mod
        ? ([
            hasMedia ? { id: 'media-section', label: t.mods.profile.mediaTitle } : null,
            hasDescription ? { id: 'desc-section', label: t.mods.profile.descriptionTitle } : null,
            hasInstallationGuide
              ? { id: 'install-section', label: t.mods.profile.installationGuideTitle }
              : null,
            hasKnownIssues
              ? { id: 'issues-section', label: t.mods.profile.knownIssuesTitle }
              : null,
            hasPrivacy ? { id: 'privacy-section', label: t.mods.profile.privacyTitle } : null,
            hasDisclaimer
              ? { id: 'disclaimer-section', label: t.mods.profile.disclaimerTitle }
              : null,
            hasCredits ? { id: 'credits-section', label: t.mods.profile.creditsTitle } : null,
            hasDownloads ? { id: 'downloads-section', label: t.mods.profile.downloadsTitle } : null,
          ].filter(Boolean) as { id: string; label: string }[])
        : [],
    [
      mod,
      hasMedia,
      hasDescription,
      hasInstallationGuide,
      hasKnownIssues,
      hasPrivacy,
      hasDisclaimer,
      hasCredits,
      hasDownloads,
      t,
    ]
  );

  const navItemIds = useMemo(() => navItems.map((item) => item.id), [navItems]);
  const activeSectionId = useActiveSection(navItemIds);

  let activeNavId = userSelectedNavId || activeSectionId;
  if (userSelectedNavId && userSelectedNavId === activeSectionId) {
    setUserSelectedNavId(null);
    activeNavId = activeSectionId;
  }

  useEffect(() => {
    if (!userSelectedNavId) return;
    const handleScroll = () => {
      setUserSelectedNavId(null);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [userSelectedNavId]);

  const handleNavItemClick = (id: string) => {
    setUserSelectedNavId(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.modProfileWrapper}>
        <div className={`${styles.container} ${className}`.trim()}>
          <div className={styles.messageCard}>
            <Typography variant="h2">{t.app.loading}</Typography>
          </div>
        </div>
      </div>
    );
  }

  if (error || !mod) {
    return (
      <div className={styles.modProfileWrapper}>
        <div className={`${styles.container} ${className}`.trim()}>
          <div className={styles.messageCard}>
            <Typography variant="h2">{t.mods.noResults}</Typography>
            <Link to={APP_ROUTES.MODS} className={styles.downloadActionBtn}>
              {t.mods.profile.backToMods}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const validTags = Array.isArray(mod.tags)
    ? mod.tags.map((tag) => (typeof tag === 'string' ? tag.trim() : '')).filter(Boolean)
    : [];

  return (
    <div className={styles.modProfileWrapper}>
      {bgUrl && (
        <div
          key={bgUrl}
          className={`${styles.pageBlurredBackdrop} ${styles.fadeIn}`}
          style={{ backgroundImage: toCssUrl(bgUrl) }}
          aria-hidden="true"
          data-testid="mod-profile-blurred-backdrop"
        />
      )}
      {bgUrl && (
        <div
          className={styles.pageBackdropOverlay}
          aria-hidden="true"
          data-testid="mod-profile-backdrop-overlay"
        />
      )}
      <div className={`${styles.container} ${className}`.trim()} data-testid="mod-profile-screen">
        {/* Back to Mods Navigation */}
        <Link to={APP_ROUTES.MODS} className={styles.backButton}>
          <span>{t.mods.profile.backToMods}</span>
        </Link>

        <div className={styles.profileLayout}>
          <div className={styles.mainColumn}>
            {/* Hero Header */}
            <header className={styles.heroHeader}>
              <div className={styles.heroContent}>
                <div className={styles.heroLeft}>
                  <div className={styles.titleContainer}>
                    <Typography variant="h1" className={styles.title}>
                      {mod.title}
                    </Typography>
                    {(mod.modAuthor || mod.author || mod.gameTitle) && (
                      <p className={styles.metaSubtitle}>
                        {mod.modAuthor || mod.author
                          ? t.mods.moddedBy({ author: (mod.modAuthor || mod.author) as string })
                          : mod.gameTitle}
                      </p>
                    )}
                  </div>

                  <div className={styles.badgesRow}>
                    <span
                      className={`${styles.badge} ${
                        mod.category === ModCategory.VR
                          ? styles.badgeVr
                          : mod.category === ModCategory.HEAD_TRACKING
                            ? styles.badgeVr
                            : styles.badgeUevr
                      }`}
                    >
                      {mod.category === ModCategory.VR
                        ? t.nav.vrMods
                        : mod.category === ModCategory.HEAD_TRACKING
                          ? t.nav.headTrackingMods
                          : t.nav.uevrMods}
                    </span>
                    {mod.status && (
                      <span className={`${styles.badge} ${styles.badgeStatus}`}>{mod.status}</span>
                    )}
                    {mod.version && (
                      <span className={`${styles.badge} ${styles.badgeMeta}`}>v{mod.version}</span>
                    )}
                    {mod.rating !== undefined && mod.rating !== null && (
                      <span className={`${styles.badge} ${styles.badgeMeta}`}>⭐ {mod.rating}</span>
                    )}
                    {mod.downloads !== undefined && mod.downloads !== null && (
                      <span className={`${styles.badge} ${styles.badgeMeta}`}>
                        ⬇ {mod.downloads.toLocaleString()}
                      </span>
                    )}
                  </div>

                  {validTags.length > 0 && (
                    <div className={styles.tagsRow}>
                      {validTags.map((tag) => (
                        <span key={tag} className={styles.tagChip}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {(mod.storePage || mod.thumbnailUrl) && (
                  <div className={styles.heroRight}>
                    {mod.storePage && (
                      <a
                        href={sanitizeUrl(mod.storePage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.storeButton}
                        aria-label={t.mods.profile.storePage}
                        title={t.mods.profile.storePage}
                      >
                        <svg
                          className={styles.storeIcon}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M19 6h-2c0-2.76-2.24-5-5-5S7 3.24 7 6H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-7-3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3zm0 10c-2.76 0-5-2.24-5-5h2c0 1.66 1.34 3 3 3s3-1.34 3-3h2c0 2.76-2.24 5-5 5z" />
                        </svg>
                        <span className={styles.storeText}>{t.mods.profile.storePage}</span>
                        <svg
                          className={styles.externalIcon}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3m-2 16H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z" />
                        </svg>
                      </a>
                    )}

                    {mod.thumbnailUrl && (
                      <div className={styles.heroThumbnailContainer}>
                        <img
                          src={getThumbnailSrc(mod.thumbnailUrl)}
                          alt={t.mods.profile.thumbnailAlt({ title: mod.title })}
                          className={styles.heroThumbnailImg}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </header>

            {/* Media Showcase (Unified Videos & Images Gallery) */}
            {hasMedia && activeMedia && (
              <section
                id="media-section"
                className={styles.mediaCard}
                aria-label={t.mods.profile.mediaTitle}
              >
                <div className={styles.mediaHeader}>
                  <Typography
                    variant="h2"
                    className={`${styles.sectionTitle} ${styles.mediaSectionTitle}`}
                  >
                    {t.mods.profile.mediaTitle}
                  </Typography>
                </div>

                <div className={styles.galleryWrapper}>
                  {activeMedia.type === 'video' ? (
                    <div className={styles.videoWrapperContainer}>
                      <div className={styles.videoWrapper}>
                        <iframe
                          className={styles.videoIframe}
                          src={activeMedia.src}
                          title={t.mods.profile.videoPreviewFor({ title: mod.title })}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={styles.activeImageContainer}>
                      <img
                        src={activeMedia.src}
                        alt={activeMedia.alt}
                        className={styles.activeImage}
                      />
                    </div>
                  )}

                  {mediaList.length > 1 && (
                    <div className={styles.thumbnailsContainer}>
                      <button
                        type="button"
                        className={`${styles.scrollBtn} ${styles.scrollBtnLeft}`}
                        onClick={handlePrevMedia}
                        aria-label={t.mods.profile.scrollThumbnailsLeft}
                      >
                        ‹
                      </button>

                      <div
                        ref={thumbnailsRowRef}
                        className={styles.thumbnailsRow}
                        role="group"
                        aria-label={t.mods.profile.galleryThumbnails}
                      >
                        {mediaList.map((item, idx) => (
                          <button
                            key={item.id}
                            type="button"
                            className={`${styles.thumbnailBtn} ${
                              item.type === 'video' ? styles.videoThumbnailBtn : ''
                            } ${idx === selectedMediaIndex ? styles.thumbnailBtnActive : ''}`}
                            onClick={() => handleSelectMedia(idx)}
                            aria-label={
                              item.type === 'video'
                                ? t.mods.profile.showVideoNamed({ index: idx + 1 })
                                : t.mods.profile.showImageNamed({
                                    index: idx + 1 - youtubeEmbedUrls.length,
                                  })
                            }
                          >
                            {item.type === 'video' ? (
                              item.thumbnailUrl ? (
                                <div className={styles.videoThumbPreview}>
                                  <img
                                    src={item.thumbnailUrl}
                                    alt={item.alt}
                                    className={styles.thumbnailImg}
                                  />
                                  <div className={styles.playOverlay}>
                                    <svg
                                      className={styles.playIcon}
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                    >
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  </div>
                                  <span className={styles.videoBadge}>{item.label}</span>
                                </div>
                              ) : (
                                <span className={styles.videoLabelOnly}>🎬 {item.label}</span>
                              )
                            ) : (
                              <img
                                src={item.thumbnailUrl}
                                alt={t.mods.profile.galleryThumbnailAlt({
                                  index: idx + 1 - youtubeEmbedUrls.length,
                                })}
                                className={styles.thumbnailImg}
                              />
                            )}
                          </button>
                        ))}
                      </div>

                      <button
                        type="button"
                        className={`${styles.scrollBtn} ${styles.scrollBtnRight}`}
                        onClick={handleNextMedia}
                        aria-label={t.mods.profile.scrollThumbnailsRight}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </div>
              </section>
            )}

            {(hasDescription ||
              hasInstallationGuide ||
              hasKnownIssues ||
              hasPrivacy ||
              hasDisclaimer ||
              hasCredits ||
              hasDownloads) && (
              <div className={styles.sectionsLayout}>
                {/* Description Section */}
                {hasDescription && (
                  <CollapsibleSection
                    id="desc-section"
                    ariaLabelledBy="desc-heading"
                    title={
                      <Typography variant="h2" id="desc-heading" className={styles.sectionTitle}>
                        {t.mods.profile.descriptionTitle}
                      </Typography>
                    }
                  >
                    <FormattedText text={mod.description} collapsibleHeadings />
                  </CollapsibleSection>
                )}

                {/* Installation Guide Section */}
                {hasInstallationGuide && (
                  <CollapsibleSection
                    id="install-section"
                    ariaLabelledBy="install-heading"
                    title={
                      <Typography variant="h2" id="install-heading" className={styles.sectionTitle}>
                        {t.mods.profile.installationGuideTitle}
                      </Typography>
                    }
                  >
                    <FormattedText text={mod.installationGuide} collapsibleHeadings />
                  </CollapsibleSection>
                )}

                {/* Known Issues Section */}
                {hasKnownIssues && (
                  <CollapsibleSection
                    id="issues-section"
                    ariaLabelledBy="issues-heading"
                    isKnownIssues
                    title={
                      <Typography
                        variant="h2"
                        id="issues-heading"
                        className={`${styles.sectionTitle} ${styles.knownIssuesTitle}`}
                      >
                        {t.mods.profile.knownIssuesTitle}
                      </Typography>
                    }
                  >
                    <FormattedText text={mod.knownIssues} collapsibleHeadings />
                  </CollapsibleSection>
                )}

                {/* Privacy Policy Section (EXCEPT - NOT COLLAPSED) */}
                {hasPrivacy && (
                  <CollapsibleSection
                    id="privacy-section"
                    ariaLabelledBy="privacy-heading"
                    isCollapsible={false}
                    title={
                      <Typography variant="h2" id="privacy-heading" className={styles.sectionTitle}>
                        {t.mods.profile.privacyTitle}
                      </Typography>
                    }
                  >
                    <FormattedText text={mod.privacy} collapsibleHeadings={false} />
                  </CollapsibleSection>
                )}

                {/* Software Disclaimer Section (EXCEPT - NOT COLLAPSED) */}
                {hasDisclaimer && (
                  <CollapsibleSection
                    id="disclaimer-section"
                    ariaLabelledBy="disclaimer-heading"
                    isCollapsible={false}
                    title={
                      <Typography
                        variant="h2"
                        id="disclaimer-heading"
                        className={styles.sectionTitle}
                      >
                        {t.mods.profile.disclaimerTitle}
                      </Typography>
                    }
                  >
                    <FormattedText text={mod.disclaimer} collapsibleHeadings={false} />
                  </CollapsibleSection>
                )}

                {/* Mod Credits & Attributions Section (EXCEPT - NOT COLLAPSED) */}
                {hasCredits && (
                  <CollapsibleSection
                    id="credits-section"
                    ariaLabelledBy="credits-heading"
                    isCollapsible={false}
                    title={
                      <Typography variant="h2" id="credits-heading" className={styles.sectionTitle}>
                        {t.mods.profile.creditsTitle}
                      </Typography>
                    }
                  >
                    <FormattedText text={mod.credits} collapsibleHeadings={false} />
                  </CollapsibleSection>
                )}

                {/* Multi-Download Options Section */}
                {hasDownloads && (
                  <CollapsibleSection
                    id="downloads-section"
                    ariaLabelledBy="downloads-heading"
                    title={
                      <Typography
                        variant="h2"
                        id="downloads-heading"
                        className={styles.sectionTitle}
                      >
                        {t.mods.profile.downloadsTitle}
                      </Typography>
                    }
                  >
                    <div className={styles.downloadsGrid}>
                      {downloadOptions.map((opt, index) => {
                        const isExternal = isExternalLinkType(opt.linkType);
                        const badgeLabel = getLinkTypeLabel(opt.linkType, t);
                        return (
                          <div key={opt.name + index} className={styles.downloadCard}>
                            <div className={styles.downloadCardHeader}>
                              <span className={styles.downloadName}>{opt.name}</span>
                              <div className={styles.downloadBadgeRow}>
                                <span
                                  className={`${styles.downloadBadge} ${
                                    isExternal
                                      ? styles.downloadBadgeExternal
                                      : styles.downloadBadgeInternal
                                  }`}
                                >
                                  {badgeLabel}
                                </span>
                              </div>
                              {opt.description && (
                                <p className={styles.downloadDescription}>{opt.description}</p>
                              )}
                            </div>

                            <a
                              href={sanitizeUrl(opt.link)}
                              className={styles.downloadActionBtn}
                              target={isExternal ? '_blank' : undefined}
                              rel={isExternal ? 'noopener noreferrer' : undefined}
                              aria-label={
                                isExternal
                                  ? `${t.mods.profile.downloadNamed({ name: opt.name })} (${t.mods.thirdPartyNotice})`
                                  : t.mods.profile.downloadNamed({ name: opt.name })
                              }
                            >
                              <svg
                                className={styles.downloadIcon}
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path d={getLinkTypeIconPath(opt.linkType)} />
                              </svg>
                              <span>{t.mods.profile.downloadNamed({ name: opt.name })}</span>
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleSection>
                )}
              </div>
            )}
          </div>

          {/* Side In-Page Navigation */}
          <PageSideNav
            items={navItems}
            activeId={activeNavId}
            onItemClick={handleNavItemClick}
            downloadOptions={downloadOptions}
          />
        </div>
      </div>
    </div>
  );
};
